import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../../components/Header';
import Search from '../../components/Search';
import { Link } from 'react-router-dom';
import {useRecoilValue} from "recoil";
import {fetchAPIResponseSelector} from "../../store/user.atom";
import {CategoryDTO} from "../../dtos/category.dto";
import {BaseItemDTO} from "../../dtos/item.dto";
import {useNavigate} from "react-router";
import {toast} from "react-toastify";

type Item = {
    id: string;
    name: string;
    base_price: number;
    image: string;
};

type Props = {};

const Items: React.FC<Props> = () => {
    const navigate = useNavigate();

    const [search, setSearch] = useState<string>('');
    const [items, setItems] = useState<Item[]>([]);
    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const observer = useRef<IntersectionObserver | null>(null);
    const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);
    const [category, setCategory] = useState<string | undefined>(undefined);
    const [categories, setCategories] = useState<CategoryDTO[]>([]);
    const [priceIncrease, setPriceIncrease] = useState<string>('');
    const [applyingIncrease, setApplyingIncrease] = useState<boolean>(false);

    useEffect(() => {
        fetchItems(true)
    }, [category, search]);

    useEffect(() => {
        if(page !== 1) {
            fetchItems(false)
        }
    }, [page]);

    const fetchItems = async (reset: boolean = false) => {
        try {
            let fetchUrl = `/items/admin?search=${search}&size=${size}`;

            if(category !== undefined) {
                fetchUrl += `&category=${category}`
            }

            if(reset) {
                fetchUrl += `&page=1`
            } else {
                fetchUrl += `&page=${page}`
            }

            const response = await fetchAPIResponse<{
                pagination_options: any,
                contents: BaseItemDTO[]
            }>(fetchUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
            });

            setItems(prevItems => (reset ? response.data.contents : [...prevItems, ...response.data.contents]));
            setTotalPages(response.data.pagination_options.total_pages);

            if(reset) {
                setPage(1)
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const lastItemRef = useCallback((node: HTMLDivElement | null) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && page < totalPages) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [page, totalPages]);

    const handleEdit = (itemId: any) => {
        navigate(`/items/edit/${itemId}`);
    };

    const handleBulkPriceIncrease = async () => {
        const percentage = parseFloat(priceIncrease);

        if (isNaN(percentage) || percentage <= -100) {
            toast.error('Please enter a valid percentage.');
            return;
        }

        const confirmIncrease = window.confirm(
            `Are you sure you want to change the price of every item by ${percentage}%? This cannot be undone.`
        );
        if (!confirmIncrease) {
            return;
        }

        try {
            setApplyingIncrease(true);

            const response = await fetchAPIResponse<{ modified_count: number }>('/items/admin/bulk-price', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ percentage })
            });

            toast.success(`Updated ${response.data.modified_count} item price(s) by ${percentage}%.`);
            setPriceIncrease('');
            setPage(1);
            setItems([]);
            fetchItems(true);
        } catch (error) {
            toast.error((error as Error).message);
        } finally {
            setApplyingIncrease(false);
        }
    };

    const handleDelete = async (itemId: any) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this item?');
        if (confirmDelete) {
            try {
                await fetchAPIResponse(`/items/admin/${itemId}`, {
                    method: 'DELETE',
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                setPage(1)
                setItems([])
                fetchItems(true);
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetchAPIResponse<CategoryDTO[]>('/categories', {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                });

                setCategories(response.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchCategories();
    }, []);

    return (
        <div className='flex flex-col items-center w-full h-full overflow-auto py-10 gap-6'>
            <Header title={'Items'} button={true} buttonTitle={'Add item'} action={'/items/add'} />
            <div className="zbh-card w-11/12 p-8">
                <Search search={search} setSearch={setSearch} placeholderText={'Item'}/>
                <div className='mt-2 mb-5 flex flex-wrap items-end justify-between gap-4'>
                    <div className="flex flex-col">
                        <label className="zbh-eyebrow mb-1.5">Category</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className='zbh-input w-56'
                        >
                            <option value="">All categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='flex items-end gap-2'>
                        <div className="flex flex-col">
                            <label className="zbh-eyebrow mb-1.5">Bulk price change</label>
                            <div className='relative'>
                                <input
                                    type='number'
                                    placeholder='e.g. 10'
                                    value={priceIncrease}
                                    onChange={(e) => setPriceIncrease(e.target.value)}
                                    className='zbh-input w-32 pr-8'
                                />
                                <span className='absolute right-3 top-1/2 -translate-y-1/2 text-categoriesTextColor'>%</span>
                            </div>
                        </div>
                        <button
                            onClick={handleBulkPriceIncrease}
                            disabled={applyingIncrease}
                            className='zbh-btn-primary'
                        >
                            {applyingIncrease ? 'Applying…' : 'Raise all prices'}
                        </button>
                    </div>
                </div>
                <div className='w-full'>
                    <div className='grid grid-cols-12 gap-4 px-2'>
                        <div className="zbh-th col-span-7">Name</div>
                        <div className="zbh-th col-span-2">Price</div>
                        <div className="zbh-th col-span-3 text-right">Actions</div>
                    </div>
                    <div className="zbh-tear mt-2" />
                    {items.map((item, index) => (
                        <div
                            key={item.id}
                            className='grid grid-cols-12 items-center gap-4 px-2 py-3 border-b border-hair last:border-0 transition hover:bg-canvas/60 rounded-lg'
                            ref={index === items.length - 1 ? lastItemRef : null}
                        >
                            <div className='col-span-7 flex items-center gap-3 min-w-0'>
                                <img src={item.image} alt={item.name} className='h-12 w-12 shrink-0 rounded-lg object-cover border border-hair'/>
                                <p className='truncate text-sm font-medium text-ink'>{item.name}</p>
                            </div>
                            <div className='col-span-2'>
                                <span className='zbh-figure text-sm font-bold text-ink'>${item.base_price}</span>
                            </div>
                            <div className='col-span-3 flex items-center justify-end gap-2'>
                                <button onClick={() => handleEdit(item.id)} className='zbh-btn-ghost px-4 py-1.5 text-xs'>Edit</button>
                                <button onClick={() => handleDelete(item.id)} className='zbh-btn-danger px-4 py-1.5 text-xs'>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Items;
